/**
 * marketingRoutes.tsx — public/unauthenticated marketing + legal routes
 * Extracted from App.tsx in #2470 (pure refactor, no behaviour changes).
 *
 * Covers: /, /pricing, /faq, /about, /templates, /changelog, /try,
 *         /share/:token, /docs, /docs/api (#3131), /blog,
 *         /privacy, /terms, /cookies, /legal/*, /404-/503 error pages.
 */
import React, { lazy, Suspense } from "react";
import { Route, Navigate } from "react-router-dom";

import { FleetLanding } from "../pages/FleetLanding";
import { Error404 } from "../pages/errors/Error404";
import { Error403 } from "../pages/errors/Error403";
import { Error500 } from "../pages/errors/Error500";
import { Error503 } from "../pages/errors/Error503";
import type { PageFallbackProps } from "./types";

// Lazy-loaded marketing pages
const FleetPricing    = lazy(() => import("../pages/FleetPricing"));
const FleetFaq        = lazy(() => import("../pages/FleetFaq"));
const FleetAbout      = lazy(() => import("../pages/FleetAbout"));
const FleetTemplates  = lazy(() => import("../pages/FleetTemplates"));
const FleetChangelog  = lazy(() => import("../pages/FleetChangelog").then(m => ({ default: m.FleetChangelog })));
const DemoPage        = lazy(() => import("../pages/DemoPage").then(m => ({ default: m.DemoPage })));
const PublicSharePage = lazy(() => import("../pages/PublicSharePage").then(m => ({ default: m.PublicSharePage })));
// #3131 — public API documentation page (WS verb table + AsyncAPI 2.x link).
const DocsApi         = lazy(() => import("../pages/DocsApi").then(m => ({ default: m.DocsApi })));

// Legal
const FleetPrivacy = lazy(() => import("../pages/FleetPrivacy").then(m => ({ default: m.FleetPrivacy })));
const FleetTerms   = lazy(() => import("../pages/FleetTerms").then(m => ({ default: m.FleetTerms })));
const FleetCookies = lazy(() => import("../pages/FleetCookies").then(m => ({ default: m.FleetCookies })));
const FleetDpa     = lazy(() => import("../pages/FleetDpa").then(m => ({ default: m.FleetDpa })));

interface Props {
  PageFallback: React.ComponentType<PageFallbackProps>;
}

/** Returns the array of public/marketing <Route> elements. */
export function getMarketingRoutes({ PageFallback }: Props): React.ReactElement[] {
  const F = <PageFallback />;

  return [
    // Landing page is rendered by RootEntry (handles auth fork) — not here
    // Pricing + discovery
    <Route key="pricing"    path="/pricing"   element={<Suspense fallback={F}><FleetPricing /></Suspense>} />,
    <Route key="faq"        path="/faq"       element={<Suspense fallback={F}><FleetFaq /></Suspense>} />,
    <Route key="about"      path="/about"     element={<Suspense fallback={F}><FleetAbout /></Suspense>} />,
    <Route key="templates"  path="/templates" element={<Suspense fallback={F}><FleetTemplates /></Suspense>} />,
    <Route key="changelog"  path="/changelog" element={<Suspense fallback={F}><FleetChangelog /></Suspense>} />,
    // #2317 — demo hive (try.2hives.ai also targets /try)
    <Route key="try"        path="/try"       element={<Suspense fallback={F}><DemoPage /></Suspense>} />,
    // #2233 — public read-only card share view
    <Route key="share"      path="/share/:token" element={<Suspense fallback={F}><PublicSharePage /></Suspense>} />,
    // #3131 — Public API documentation. /docs lands on /docs/api (the only doc
    // page that exists in v0.1); /blog stub stays in place until that lands.
    <Route key="docs-api"  path="/docs/api" element={<Suspense fallback={F}><DocsApi /></Suspense>} />,
    <Route key="docs"      path="/docs"     element={<Navigate to="/docs/api" replace />} />,
    <Route key="blog"      path="/blog"     element={<Navigate to="/" replace />} />,

    // Legal pages
    <Route key="privacy"        path="/privacy"        element={<Suspense fallback={F}><FleetPrivacy /></Suspense>} />,
    <Route key="terms"          path="/terms"          element={<Suspense fallback={F}><FleetTerms /></Suspense>} />,
    <Route key="cookies"        path="/cookies"        element={<Suspense fallback={F}><FleetCookies /></Suspense>} />,
    // /legal/* aliases — anon-accessible per GDPR / smoke-report 2026-06-01
    <Route key="legal-privacy"  path="/legal/privacy"  element={<Suspense fallback={F}><FleetPrivacy /></Suspense>} />,
    <Route key="legal-terms"    path="/legal/terms"    element={<Suspense fallback={F}><FleetTerms /></Suspense>} />,
    <Route key="legal-cookies"  path="/legal/cookies"  element={<Suspense fallback={F}><FleetCookies /></Suspense>} />,
    <Route key="legal-dpa"      path="/legal/dpa"      element={<Suspense fallback={F}><FleetDpa /></Suspense>} />,

    // #2454 — branded error pages (CloudFront custom_error_responses targets these SPA routes)
    <Route key="err-404" path="/404" element={<Error404 />} />,
    <Route key="err-403" path="/403" element={<Error403 />} />,
    <Route key="err-500" path="/500" element={<Error500 />} />,
    <Route key="err-503" path="/503" element={<Error503 />} />,
  ];
}
